/**
 * 01 · Analyzing — the beat between the last question and the email step.
 *
 * Four passes fill while the person reads reviews from people who took the same
 * test. The button stays disabled until the last pass lands, so the screen is
 * honest about being busy rather than decorative.
 *
 * This sits BEFORE the email step on purpose: the work is visibly done first,
 * and only then do we ask where to send it.
 */
import React from 'react';
import { Icon } from '../app/icons';
import { c, hair, f, ACT } from '../app/tokens';
import { Col, Row, Meta, Muted, Appear, usePrefersReducedMotion } from '../app/primitives';
import { useTt } from '../i18n/useT';
import { reviews } from './copy';
import { Carousel, Cta, OK, ReviewCard, Screen, Serif, StickyActions, useWide } from './ui';

const PASS_KEYS = [
    'pw.an.step.answers',
    'pw.an.step.patterns',
    'pw.an.step.directions',
    'pw.an.step.map',
];

/** How long a review stays up before the carousel turns itself. */
const REVIEW_MS = 4200;

/** How long one pass takes, in ms. Each run picks a fresh value in this range. */
const PASS_MS: [number, number] = [900, 1900];

/** The beat between one pass landing and the next starting. */
const GAP_MS: [number, number] = [220, 520];

/** Segments a pass is divided into; each gets its own share of the fill. */
const SEGMENTS = 7;

const between = ([lo, hi]: [number, number]) => lo + Math.random() * (hi - lo);

/**
 * A monotone speed profile: cumulative fractions of the bar filled at the end of
 * each equal slice of time.
 *
 * Equal weights would give a constant crawl. Random ones make the bar surge and
 * stall the way real work does — a slice that drew 1.8 covers three times the
 * ground of one that drew 0.6, in the same number of milliseconds.
 */
function speedProfile(): number[] {
    const weights = Array.from({ length: SEGMENTS }, () => 0.35 + Math.random() * 1.65);
    const total = weights.reduce((a, b) => a + b, 0);
    let acc = 0;

    return weights.map((w) => {
        acc += w / total;

        return Math.min(1, acc);
    });
}

/** One pass's slot in the run: when it starts, when it lands, how it fills. */
interface PassPlan {
    key: string;
    /** Milliseconds from the start of the run. */
    start: number;
    end: number;
    profile: number[];
}

/** Lay the passes end to end, each with its own duration and a gap after it. */
function schedule(): PassPlan[] {
    let t = 0;

    return PASS_KEYS.map((key) => {
        const start = t;
        const end = start + between(PASS_MS);

        t = end + between(GAP_MS);

        return { key, start, end, profile: speedProfile() };
    });
}

/** How full one pass's bar is at `elapsed`, 0–1. */
function fillAt(pass: PassPlan, elapsed: number): number {
    if (elapsed <= pass.start) return 0;
    if (elapsed >= pass.end) return 1;

    const u = (elapsed - pass.start) / (pass.end - pass.start);
    const slice = u * SEGMENTS;
    const i = Math.floor(slice);
    const from = i === 0 ? 0 : pass.profile[i - 1];

    return from + (pass.profile[i] - from) * (slice - i);
}

/**
 * Milliseconds elapsed, and the run's total length.
 *
 * One pass at a time: nothing starts until the one before it has landed and its
 * gap has passed, so the screen reads as four steps rather than four bars.
 */
function useRun(): { elapsed: number; passes: PassPlan[]; total: number } {
    const reduced = usePrefersReducedMotion();
    // Rolled once per mount, so the timing differs between visits but never
    // jumps around inside one.
    const passes = React.useMemo(schedule, []);
    const total = passes[passes.length - 1].end;
    const [elapsed, setElapsed] = React.useState(reduced ? total : 0);

    React.useEffect(() => {
        if (reduced) {
            setElapsed(total);

            return undefined;
        }

        let raf = 0;
        let start = 0;
        let settled = false;

        const tick = (now: number) => {
            if (!start) start = now;
            const e = Math.min(total, now - start);

            setElapsed(e);
            if (e < total) raf = requestAnimationFrame(tick);
            else settled = true;
        };

        raf = requestAnimationFrame(tick);

        // Nobody may be stranded on a half-filled bar because the tab was
        // backgrounded and rAF stopped firing.
        const fb = setTimeout(() => {
            if (!settled) {
                cancelAnimationFrame(raf);
                setElapsed(total);
            }
        }, total + 500);

        return () => {
            clearTimeout(fb);
            cancelAnimationFrame(raf);
        };
    }, [reduced, total]);

    return { elapsed, passes, total };
}

const Pass: React.FC<{ label: string; pct: number; wide?: boolean }> = ({ label, pct, wide }) => {
    const done = pct >= 100;

    return (
        <Col gap={wide ? 10 : 11}>
            <Row style={{ gap: 12, justifyContent: 'space-between', alignItems: 'center' }}>
                <span
                    style={{ fontSize: wide ? 14.5 : 15, color: done ? c.textPri : c.text, fontWeight: 400, flex: 1 }}
                >
                    {label}
                </span>
                {done ? (
                    <div
                        style={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            background: OK,
                            display: 'grid',
                            placeItems: 'center',
                            flex: '0 0 auto',
                        }}
                    >
                        <Icon k="check" size={12} color="#0a0a0a" />
                    </div>
                ) : (
                    <span
                        style={{
                            fontFamily: f.mono,
                            fontSize: 14,
                            color: ACT,
                            fontVariantNumeric: 'tabular-nums',
                            flex: '0 0 auto',
                        }}
                    >
                        {pct}%
                    </span>
                )}
            </Row>
            <div style={{ height: 4, borderRadius: 2, background: c.track, overflow: 'hidden' }}>
                <div
                    style={{
                        width: `${pct}%`,
                        height: '100%',
                        background: done ? OK : ACT,
                        boxShadow: `0 0 8px ${done ? OK : ACT}55`,
                        // Width is driven per frame, so a CSS transition on it
                        // would lag behind and average out the very surges and
                        // stalls the speed profile exists to produce.
                        transition: 'background 300ms',
                    }}
                />
            </div>
        </Col>
    );
};

const Analyzing: React.FC<{ onNext: () => void }> = ({ onNext }) => {
    const { t } = useTt();
    const wide = useWide();
    const { elapsed, passes: scheduled, total } = useRun();
    const list = React.useMemo(() => reviews(t), [t]);

    const passes = scheduled.map((p) => ({
        key: p.key,
        label: t(p.key),
        pct: Math.round(fillAt(p, elapsed) * 100),
    }));
    const done = elapsed >= total;

    // Four lines on a phone: enough to judge the voice, short enough that the
    // card cannot push the step's own button off the screen.
    const reviewCards = list.map((r) => (
        <ReviewCard key={r.name + r.title} review={r} wide={wide} clamp={wide ? undefined : 4} />
    ));

    if (wide) {
        return (
            <Screen wide style={{ padding: '48px 40px 56px' }}>
                <Appear>
                    <Col style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }} gap={13}>
                        <Meta style={{ fontSize: 10, color: ACT, letterSpacing: '0.18em' }}>
                            {t('pw.an.d.eyebrow')}
                        </Meta>
                        <Serif size={40} style={{ textAlign: 'center', lineHeight: 1.14 }}>
                            {t('pw.an.d.title')}
                        </Serif>
                        <Muted style={{ fontSize: 15, lineHeight: 1.55 }}>{t('pw.an.d.sub')}</Muted>
                    </Col>
                </Appear>

                <Col style={{ marginTop: 38 }} gap={34}>
                    <Appear delay={120}>
                        <div
                            style={{
                                width: '100%',
                                maxWidth: 720,
                                margin: '0 auto',
                                background: c.card,
                                border: hair,
                                borderRadius: 20,
                                padding: '22px 24px',
                            }}
                        >
                            <Col gap={22}>
                                {passes.map((p) => (
                                    <Pass key={p.key} label={p.label} pct={p.pct} wide />
                                ))}
                            </Col>
                        </div>
                    </Appear>

                    {reviewCards.length > 0 && (
                        <Appear delay={240}>
                            <Col style={{ maxWidth: 720, margin: '0 auto', width: '100%' }} gap={16}>
                                <Col gap={7}>
                                    <span
                                        style={{
                                            fontSize: 16,
                                            color: c.textPri,
                                            fontWeight: 400,
                                            letterSpacing: '-0.015em',
                                        }}
                                    >
                                        {t('pw.an.reviews.title')}
                                    </span>
                                    <Muted style={{ fontSize: 13 }}>{t('pw.an.reviews.sub')}</Muted>
                                </Col>
                                <Carousel pad={0} gap={16} arrows auto={REVIEW_MS}>
                                    {reviewCards}
                                </Carousel>
                            </Col>
                        </Appear>
                    )}

                    <Cta
                        label={done ? t('pw.continue') : t('pw.an.preparing')}
                        disabled={!done}
                        onClick={onNext}
                        width={320}
                        height={54}
                    />
                </Col>
            </Screen>
        );
    }

    // One screenful, no scrolling: the passes and the reviews share the space
    // that is left after the heading and the button, and the button itself is
    // pinned to the bottom edge rather than pushed there by whatever happens to
    // sit above it.
    return (
        <Screen column>
            {/* Content stacks from the top; this column absorbs whatever is
                left over, which is what holds the button against the bottom
                edge. Any spare height therefore collects in ONE place — under
                the reviews, right above the button — instead of being torn out
                between the passes and the reviews. */}
            <Col style={{ flex: 1, minHeight: 0 }}>
                <Appear>
                    <div style={{ padding: '40px 20px 0' }}>
                        <Serif size={26} style={{ textAlign: 'center' }}>
                            {t('pw.an.title')}
                        </Serif>
                    </div>

                    <Col style={{ padding: '30px 20px 0' }} gap={20}>
                        {passes.map((p) => (
                            <Pass key={p.key} label={p.label} pct={p.pct} />
                        ))}
                    </Col>
                </Appear>

                {reviewCards.length > 0 && (
                    <div style={{ padding: '26px 0 0' }}>
                        <Col style={{ padding: '0 20px 12px' }} gap={5}>
                            <span
                                style={{ fontSize: 15, color: c.textPri, fontWeight: 400, letterSpacing: '-0.014em' }}
                            >
                                {t('pw.an.reviews.title')}
                            </span>
                            <Muted style={{ fontSize: 12 }}>{t('pw.an.reviews.sub')}</Muted>
                        </Col>
                        {/* 300px of a 390px screen: the next card peeks, which is
                            what tells a thumb there is more than one. */}
                        <Carousel card={300} auto={REVIEW_MS}>
                            {reviewCards}
                        </Carousel>
                    </div>
                )}
            </Col>

            <StickyActions>
                <Cta label={done ? t('pw.continue') : t('pw.an.preparing')} disabled={!done} onClick={onNext} />
            </StickyActions>
        </Screen>
    );
};

export default Analyzing;
